import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

// Import orphan prevention utility
import { preventOrphans } from '../utils/textUtils';

const ASSETS_BASE_URL = 'https://dyararts.com';

// Helper function to build asset URL
const buildAssetUrl = (filename) => {
  if (!filename) return '';
  return `${ASSETS_BASE_URL}${filename}`;
};

// Helper function to determine asset type from filename
const getAssetType = (filename) => {
  if (!filename) return 'image';
  const extension = filename.toLowerCase().split('.').pop();
  const videoExtensions = ['mp4', 'webm', 'ogg', 'avi', 'mov'];
  return videoExtensions.includes(extension) ? 'video' : 'image';
};

const GalleryCarouselPage = ({ assets, gallery, project }) => {
  const navigate = useNavigate();
  
  // Filter assets based on project's filter-method
  const filterMethod = project?.['filter-method'] || gallery['filter-prefix'];
  const galleryAssets = assets.filter(asset => {
    const artworkId = asset['artwork-id'] || asset.filename.split('_')[0];
    return artworkId.startsWith(filterMethod);
  });

  // Sort by artwork-id to ensure correct order
  const sortedAssets = [...galleryAssets].sort((a, b) => {
    const idA = a['artwork-id'] || '';
    const idB = b['artwork-id'] || '';
    return idA.localeCompare(idB);
  });

  // Convert assets to slides format
  const slides = sortedAssets.map((asset, index) => ({
    id: `${gallery.id}-${index + 1}`,
    type: asset.type || getAssetType(asset.filename),
    src: buildAssetUrl(asset.filename),
    alt: asset.title || `${gallery.title} - ${index + 1}`,
    title: asset.title || `${index + 1}`,
    captionTitle: asset.title || `${index + 1}`,
    captionDescription: asset.description || '',
    subHeadline: asset['sub-headline'] || '',
    mediaId: `${gallery.id}-media-${index + 1}`,
    backgroundSize: asset['background-size'] || '',
    size: asset.size || '110%',
    fit: asset.fit || 'contain',
    orientation: asset.orientation || 'vertical',
    'artwork-id': asset['artwork-id'] || '',
    slug: asset.slug || ''
  }));

  // Carousel state
  const [currentSlide, setCurrentSlide] = useState(0);
  const [autoplayProgress, setAutoplayProgress] = useState(0);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [videoProgress, setVideoProgress] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [videoAspectRatio, setVideoAspectRatio] = useState(null);
  const [paginationScrollLeft, setPaginationScrollLeft] = useState(0);
  const [showVideoControls, setShowVideoControls] = useState(false);
  const [hasAudioTrack, setHasAudioTrack] = useState(true);
  const [isLocalMuted, setIsLocalMuted] = useState(true);
  const [isGlobalAutoplayActive, setIsGlobalAutoplayActive] = useState(true);
  const [isPaginationMinimal, setIsPaginationMinimal] = useState(window.innerWidth < 768);

  const videoRef = useRef(null);
  const carouselRef = useRef(null);
  const paginationContainerRef = useRef(null);
  const screenSizeTimeoutRef = useRef(null);

  // Constants
  const PROGRESS_INCREMENT = 1;
  const PROGRESS_INTERVAL = 50;
  const VISIBLE_BUTTONS = 5;
  const BUTTON_WIDTH = 64;
  const SLIDES_AUTOPLAY_INTERVAL = 3000;

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Responsive pagination - automatically set minimal view on mobile
  useEffect(() => {
    const checkScreenSize = () => {
      // Clear any pending timeout
      if (screenSizeTimeoutRef.current) {
        clearTimeout(screenSizeTimeoutRef.current);
      }

      // Debounce: wait 300ms after resize stops before processing
      screenSizeTimeoutRef.current = setTimeout(() => {
        const newIsMinimal = window.innerWidth < 768; // md breakpoint
        setIsPaginationMinimal(newIsMinimal);
      }, 300); // 300ms debounce delay
    };

    // Check on mount
    checkScreenSize();

    // Listen for resize events with debouncing
    window.addEventListener('resize', checkScreenSize);

    return () => {
      window.removeEventListener('resize', checkScreenSize);
      if (screenSizeTimeoutRef.current) {
        clearTimeout(screenSizeTimeoutRef.current);
      }
    };
  }, []);

  // Navigation functions
  const nextSlide = useCallback(() => {
    const newSlide = currentSlide < slides.length - 1 
      ? currentSlide + 1
      : 0;
    setCurrentSlide(newSlide);
    setAutoplayProgress(0);
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
      setVideoProgress(0);
      setIsVideoPlaying(false);
    }
  }, [currentSlide, slides.length]);

  const goToSlide = (index) => {
    const validIndex = Math.max(0, Math.min(index, slides.length - 1));
    setCurrentSlide(validIndex);
    setAutoplayProgress(0);
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
      setVideoProgress(0);
      setIsVideoPlaying(false);
    }
    updatePaginationScroll(validIndex);
  };

  const toggleAutoplay = () => {
    setIsGlobalAutoplayActive(!isGlobalAutoplayActive);
  };

  const toggleLocalMute = () => {
    setIsLocalMuted(prev => {
      const next = !prev;
      if (videoRef.current) videoRef.current.muted = next;
      return next;
    });
  };

  const toggleVideoPlayback = () => {
    if (videoRef.current) {
      if (isVideoPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsVideoPlaying(!isVideoPlaying);
    }
  };

  // Autoplay functionality
  useEffect(() => {
    let interval;
    if (isGlobalAutoplayActive) {
      interval = setInterval(() => {
        setAutoplayProgress(prev => {
          if (prev >= 100) {
            const isCurrentSlideVideo = currentSlide < slides.length && slides[currentSlide].type === 'video';
            if (isCurrentSlideVideo) {
              return 0;
            } else {
              nextSlide();
              return 0;
            }
          }
          return prev + PROGRESS_INCREMENT;
        });
      }, SLIDES_AUTOPLAY_INTERVAL / 100);
    }
    return () => clearInterval(interval);
  }, [isGlobalAutoplayActive, currentSlide, nextSlide, slides, PROGRESS_INCREMENT, SLIDES_AUTOPLAY_INTERVAL]);

  // Video controls visibility
  useEffect(() => {
    const isVideoSlide = currentSlide < slides.length && slides[currentSlide].type === 'video';
    setShowVideoControls(isVideoSlide);
  }, [currentSlide, slides]);

  // Pagination scroll logic
  const updatePaginationScroll = useCallback((activeIndex) => {
    const totalButtons = slides.length;
    if (totalButtons <= VISIBLE_BUTTONS) {
      setPaginationScrollLeft(0);
      return;
    }
    const pageIndex = Math.floor(activeIndex / VISIBLE_BUTTONS);
    const scrollLeft = pageIndex * VISIBLE_BUTTONS * BUTTON_WIDTH;
    setPaginationScrollLeft(scrollLeft);
  }, [slides.length]);

  useEffect(() => {
    updatePaginationScroll(currentSlide);
  }, [currentSlide, updatePaginationScroll]);

  useEffect(() => {
    if (paginationContainerRef.current) {
      paginationContainerRef.current.scrollLeft = paginationScrollLeft;
    }
  }, [paginationScrollLeft]);

  // Video scrubbing
  const handleVideoSeek = (e) => {
    if (videoRef.current && videoRef.current.duration) {
      const rect = e.currentTarget.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const percentage = Math.max(0, Math.min(100, (clickX / rect.width) * 100));
      const newTime = (percentage / 100) * videoRef.current.duration;
      videoRef.current.currentTime = newTime;
      setVideoProgress(percentage);
    }
  };

  const handleMouseDown = (e) => {
    if (videoRef.current && videoRef.current.duration) {
      setIsDragging(true);
      handleVideoSeek(e);
    }
  };

  const handleVideoEnded = () => {
    setIsVideoPlaying(false);
    setVideoProgress(0);
    const nextSlideIndex = currentSlide < slides.length - 1 ? currentSlide + 1 : 0;
    goToSlide(nextSlideIndex);
  };

  // Video progress tracking
  useEffect(() => {
    if (currentSlide < slides.length && slides[currentSlide].type === 'video' && videoRef.current) {
      const video = videoRef.current;
      const updateProgress = () => {
        if (video.duration) {
          setVideoProgress((video.currentTime / video.duration) * 100);
        }
      };
      video.addEventListener('timeupdate', updateProgress);
      return () => video.removeEventListener('timeupdate', updateProgress);
    }
  }, [currentSlide, slides]);

  // Render slide content
  const renderSlideContent = (slide) => {
    if (slide.type === 'video') {
      return (
        <div className="relative w-full h-full">
          <video
            ref={videoRef}
            src={slide.src}
            className="carousel-video"
            muted={isLocalMuted}
            autoPlay
            onEnded={handleVideoEnded}
            onPlay={() => setIsVideoPlaying(true)}
            onPause={() => setIsVideoPlaying(false)}
            onClick={toggleVideoPlayback}
            style={{ cursor: 'pointer' }}
          />
        </div>
      );
    }
    
    return (
      <div className="relative w-full h-full">
        <div className="asset-wrapper">
          <div className="asset-inner">
            <img
              src={slide.src}
              alt={slide.alt}
              style={{
                width: slide.size,
                height: slide.size,
                objectFit: slide.fit || 'contain'
              }}
            />
          </div>
        </div>
      </div>
    );
  };

  if (slides.length === 0) {
    return (
      <div className="gallery-carousel-page">
        <p>No artwork found for this gallery.</p>
      </div>
    );
  }

  return (
    <div className="gallery-carousel-page">
      {/* Full Width Carousel */}
      <div className="section-container" data-project={gallery.slug}>
        <div className="carousel-container carousel-height-image">
          {/* Carousel Grid - 2 column layout */}
          <div className="carousel-grid">
            {/* News Column - Left side 30% */}
            <div className="news-column">
              {/* Top row - 30% height (where expanded icon visually aligns) */}
              <div className="news-top">
                {project?.['name-asset'] ? (
                  <img src={`${ASSETS_BASE_URL}${project['name-asset']}`} alt={project.name || 'Logo'} />
                ) : project?.name ? (
                  <h1>{project.name}</h1>
                ) : null}
              </div>
              {/* Bottom row - Auto height (news updates) */}
              <div className="news-bottom">
                <div className="hello">
                  {project?.['hello-1st'] && <h3>{preventOrphans(project['hello-1st'])}</h3>}
                  {project?.['hello-2nd'] && <p>{preventOrphans(project['hello-2nd'])}</p>}
                </div>
                {/* Desktop version - shown on desktop, hidden on tablet - ONLY for hero-order */}
                {project?.['filter-method'] === 'hero-order' && (
                  <div className="news-item news-item-desktop">
                    <p>Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed eleifend nulla eu facilisis commodo. Integer aliquam vestibulum metus. Proin ac lobortis nisi. Pellentesque imperdiet ut lacus eu dictum. Pellentesque tincidunt imperdiet purus sit amet tristique. Donec nec nibh mauris. Maecenas tincidunt eros et vehicula iaculis. Duis quis elementum sem, vel mattis velit.</p>
                  </div>
                )}
              </div>
            </div>

            {/* Carousel Asset View - Right side 70% */}
            <div className="carousel-asset-view w-full h-full relative z-10">
              {/* Project Details */}
              {currentSlide < slides.length && (
                <div className="project-details">
                  <h2>{preventOrphans(slides[currentSlide].captionTitle)}</h2>
                </div>
              )}

              {/* Main Asset Container */}
              <div ref={carouselRef} className="relative w-full h-full">
              {currentSlide < slides.length && (
                <div
                  key={`slide-${currentSlide}`}
                  className="slide-fade-in"
                  style={{
                    width: '100%',
                    height: '100%',
                    animation: 'slideContentFadeIn 0.2s ease-in-out'
                  }}
                >
                  {renderSlideContent(slides[currentSlide])}
                </div>
              )}
            </div>

            {/* Video Paused Overlay */}
            {currentSlide < slides.length && slides[currentSlide].type === 'video' && !isVideoPlaying && !isDragging && videoRef.current && videoRef.current.readyState >= 3 && (
              <div className="video-paused-overlay" onClick={toggleVideoPlayback} style={{ cursor: 'pointer' }}>
                <div className="paused-overlay-content">
                  <div className="play-icon">&#8227;</div>
                  <div className="paused-text">Press to Play</div>
                </div>
              </div>
            )}

            {/* UI Controls Wrapper */}
            <div className="ui-controls-wrapper">
              {/* Video Controls Container */}
              <div className={`video-controls-container ${showVideoControls ? 'show' : 'hide'}`}>
                {showVideoControls && (
                  <div className="video-controls-content">
                    <div 
                      className={`video-scrubber-container ${isDragging ? 'dragging' : ''}`}
                      onMouseDown={handleMouseDown}
                      onClick={handleVideoSeek}
                    >
                      <div 
                        className={`video-scrubber-progress ${videoProgress >= 10 ? 'show-text' : ''}`}
                        style={{ width: `${videoProgress}%` }}
                      >
                        <div className="video-progress-text">{Math.round(videoProgress)}</div>
                      </div>
                      <div 
                        className="video-scrubber-handle"
                        style={{ left: `calc(${videoProgress}% - 5px)` }}
                      ></div>
                    </div>
                    
                    {hasAudioTrack ? (
                      <button onClick={toggleLocalMute} className="video-global-mute-button">
                        {isLocalMuted ? 'Muted' : <>Audio ON</>}
                      </button>
                    ) : (
                      <button className="video-no-sound-button" disabled>
                        No Sound
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* Pagination Navigation */}
              {slides && slides.length > 0 && (
                <div className="pagination-navigation">
                  {/* Previous Button */}
                  <button
                    onClick={() => {
                      if (currentSlide === 0) {
                        goToSlide(slides.length - 1);
                      } else {
                        goToSlide(currentSlide - 1);
                      }
                    }}
                    className="indicator-buttons"
                  >
                    &larr;
                    <div></div>
                  </button>
                  
                  {/* Pagination Container */}
                  <div
                    ref={paginationContainerRef}
                    className="pagination-container"
                    style={{
                      width: isPaginationMinimal ? 'auto' : `${VISIBLE_BUTTONS * BUTTON_WIDTH}px`
                    }}
                  >
                    {isPaginationMinimal ? (
                      /* Minimal View - Slide Counter */
                      <div className="indicator-buttons-static px-16">
                        {currentSlide + 1}<span className='text-lg text-gray-400'>&nbsp;/&nbsp;</span>{slides.length}
                      </div>
                    ) : (
                      /* Full View - Slide Number Indicators */
                      slides.map((_, index) => (
                        <button
                          key={index}
                          onClick={() => {
                            if (index === currentSlide && slides[currentSlide].type === 'video') {
                              toggleVideoPlayback();
                            } else {
                              goToSlide(index);
                            }
                          }}
                          className={`indicator-buttons ${
                            slides[index].type === 'video' ? 'indicator-buttons-video' : ''
                          } ${
                            index === currentSlide ? 'active' : ''
                          } ${
                            index === currentSlide && slides[currentSlide].type === 'video'
                              ? 'indicator-buttons-playback'
                              : ''
                          }`}
                        >
                          {index === currentSlide && slides[currentSlide].type === 'video' ?
                            (isVideoPlaying ? 'Playing' : (<span>&#8227;</span>)) :
                            (index + 1)
                          }
                          <div></div>
                        </button>
                      ))
                    )}
                  </div>
                  
                  {/* Autoplay Button */}
                  <button
                    onClick={toggleAutoplay}
                    className={`autoplay-progress-button ${
                      currentSlide < slides.length && slides[currentSlide].type === 'video' ? 'disabled' : ''
                    }`}
                    disabled={currentSlide < slides.length && slides[currentSlide].type === 'video'}
                  >
                    <div className="progress-fill-container">
                      {isGlobalAutoplayActive && 
                        currentSlide < slides.length && slides[currentSlide].type !== 'video' && (
                        <div 
                          className="progress-fill"
                          style={{ width: `${autoplayProgress}%` }}
                        />
                      )}
                    </div>
                    <span className="progress-icon">
                      {currentSlide < slides.length && slides[currentSlide].type === 'video' ? (
                        <svg viewBox="0 0 24 24" className="w-6 h-6 fill-current" style={{ color: '#a7a7a7' }}>
                          <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                        </svg>
                      ) : isGlobalAutoplayActive ? (
                        <svg viewBox="0 0 24 24" className="w-6 h-6 fill-current" style={{ color: 'var(--tw-myColors-dark1)' }}>
                          <path d="M8 5v14l11-7z"/>
                        </svg>
                      ) : (
                        <svg viewBox="0 0 24 24" className="w-6 h-6 fill-current" style={{ color: '#a7a7a7' }}>
                          <path d="M8 5v14l11-7z"/>
                        </svg>
                      )}
                    </span>
                  </button>
                  
                  {/* Next Button */}
                  <button
                    onClick={() => {
                      if (currentSlide === slides.length - 1) {
                        goToSlide(0);
                      } else {
                        goToSlide(currentSlide + 1);
                      }
                    }}
                    className="indicator-buttons"
                  >
                    &rarr;
                    <div></div>
                  </button>
                </div>
              )}
            </div>
          </div>
          {/* End Carousel Asset View */}
        </div>
        {/* End Carousel Grid */}

        {/* News Item - Tablet/Mobile version - shown below grid on tablet, hidden on desktop - ONLY for hero-order */}
        {project?.['filter-method'] === 'hero-order' && (
          <div className="content-wrapper news-item-wrapper">
            <div className="news-item news-item-mobile">
              <p>Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed eleifend nulla eu facilisis commodo. Integer aliquam vestibulum metus. Proin ac lobortis nisi. Pellentesque imperdiet ut lacus eu dictum. Pellentesque tincidunt imperdiet purus sit amet tristique. Donec nec nibh mauris. Maecenas tincidunt eros et vehicula iaculis. Duis quis elementum sem, vel mattis velit.</p>
            </div>
          </div>
        )}

        </div>
        {/* End Carousel Container */}
      </div>
      {/* End Section Container */}
    </div>
  );
};

export default GalleryCarouselPage;

